import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Group,
  Loader,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconCpu,
  IconStack2,
  IconArrowsExchange,
  IconActivity,
  IconDatabase,
  IconTerminal2,
  IconHistory,
  IconAdjustments,
  IconPlayerPlay,
  IconPower,
  IconPlayerStop,
  IconTrash,
  IconAlertTriangle,
  IconNetwork,
} from "@tabler/icons-react";
import { apiDelete, apiFetch, friendlyError, type ApiError } from "../../api/client.js";
import {
  containerAction,
  formatBytes,
  formatRam,
  formatUptime,
  type ContainerAction,
  type ContainerDetail,
  type GuestInterface,
} from "./containerUtils.js";
import { StatusPill } from "../../components/StatusPill.js";
import { ResizeContainerModal } from "./ResizeContainerModal.js";
import { SnapshotsModal } from "./SnapshotsModal.js";
import { BackupsCard } from "./BackupsCard.js";

const ContainerConsoleModal = lazy(() => import("./ContainerConsoleModal.js"));

// Lazily loaded so recharts/@mantine/charts stays out of the main bundle.
const DiskActivityCard = lazy(() => import("./DiskActivityCard.js"));

const POLL_MS = 5000;

export function ContainerDetail() {
  const { vmid } = useParams<{ vmid: string }>();
  const navigate = useNavigate();
  const [ct, setCt] = useState<ContainerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resizeOpen, setResizeOpen] = useState(false);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [snapshotsOpen, setSnapshotsOpen] = useState(false);

  async function load() {
    if (!vmid) return;
    try {
      const data = await apiFetch<ContainerDetail>(`/api/containers/${vmid}`);
      setCt(data);
      setError(null);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 502 && apiErr.error === "pve_unavailable") {
        setError("Can't reach Proxmox right now — retrying…");
      } else {
        setError(friendlyError(err, "Could not load container."));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vmid]);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vmid]);

  async function runAction(action: ContainerAction, body?: { force?: boolean; timeout?: number }) {
    if (!ct) return;
    setBusy(true);
    try {
      const res = await containerAction(ct.vmid, action, body);
      notifications.show({ color: res.ok ? "green" : "red", message: res.message });
      if (res.ok) void load();
    } finally {
      setBusy(false);
    }
  }

  function confirmPower(action: "shutdown" | "stop") {
    if (!ct) return;
    const isStop = action === "stop";
    modals.openConfirmModal({
      title: isStop ? "Force stop container" : "Shut down container",
      centered: true,
      children: (
        <Text size="sm">
          {isStop ? (
            <>
              Force stop <b>{ct.name}</b>? This pulls the power and may corrupt the filesystem.
            </>
          ) : (
            <>
              Shut down <b>{ct.name}</b>? The container will be asked to exit cleanly.
            </>
          )}
        </Text>
      ),
      labels: { confirm: isStop ? "Force stop" : "Shut down", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => void runAction(action, isStop ? undefined : { force: false }),
    });
  }

  async function deleteContainer() {
    if (!ct) return;
    setBusy(true);
    try {
      await apiDelete(`/api/containers/${ct.vmid}`);
      notifications.show({ color: "green", message: "Container deleted." });
      navigate("/dashboard/containers");
    } catch (err) {
      // Surface backend's human message when present (e.g. stop_timeout's
      // "container did not stop within 30s. Try again in a moment.").
      const detail = (err as ApiError)?.details as { message?: string } | undefined;
      notifications.show({
        color: "red",
        message: detail?.message ?? "Could not delete container. Try again or contact support.",
      });
    } finally {
      setBusy(false);
    }
  }

  function confirmDelete() {
    if (!ct) return;
    modals.openConfirmModal({
      title: "Delete container",
      centered: true,
      closeOnClickOutside: false,
      closeOnEscape: false,
      children: (
        <Text size="sm">
          Permanently delete <b>{ct.name}</b> (Container ID {ct.vmid})? This destroys the container
          and all its data. <b>This cannot be undone.</b>
        </Text>
      ),
      labels: { confirm: "Delete container", cancel: "Cancel" },
      confirmProps: { color: "red" },
      groupProps: { gap: "xs" },
      onConfirm: () => void deleteContainer(),
    });
  }

  if (loading && !ct) {
    return (
      <Stack gap="xs" py="xl" align="center">
        <Loader />
        <Text c="dimmed" size="sm">
          Loading…
        </Text>
      </Stack>
    );
  }
  if (error && !ct) {
    return (
      <Stack p="md" gap="md">
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
        <div>
          <Button variant="default" size="xs" w={140} onClick={() => navigate("/dashboard/containers")}>
            Back to list
          </Button>
        </div>
      </Stack>
    );
  }
  if (!ct) return null;

  const cpuUsagePct = ct.cpu_usage_fraction > 0 ? Math.round(ct.cpu_usage_fraction * 100) : null;
  const memUsagePct =
    ct.memory_usage_bytes > 0 && ct.memory_mb > 0
      ? Math.min(100, Math.round((ct.memory_usage_bytes / (ct.memory_mb * 1024 * 1024)) * 100))
      : null;
  const swapUsagePct =
    ct.swap_usage_bytes > 0 && ct.swap_mb > 0
      ? Math.min(100, Math.round((ct.swap_usage_bytes / (ct.swap_mb * 1024 * 1024)) * 100))
      : null;

  return (
    <Stack gap="md">
      <Breadcrumbs>
        <Anchor component={Link} to="/dashboard/containers" size="sm">
          Containers
        </Anchor>
        <Text size="sm" fw={600}>
          {ct.name}
        </Text>
      </Breadcrumbs>

      {/* Header card: identity + primary actions */}
      <Card padding="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Stack gap={4}>
            <Group gap="sm" align="center">
              <Title order={2}>{ct.name}</Title>
              <StatusPill status={ct.status} />
              {ct.snapshot_count > 0 && (
                <Tooltip
                  label={`This container has ${ct.snapshot_count} snapshot${ct.snapshot_count === 1 ? "" : "s"}. Snapshots are protective but can slow disk I/O if many accumulate. Up to 5 snapshots can be kept — review periodically and remove unneeded ones.`}
                  position="bottom"
                  withArrow
                  multiline
                  w={260}
                >
                  <Badge
                    size="sm"
                    variant="light"
                    color={ct.snapshot_count >= 5 ? "red" : ct.snapshot_count >= 3 ? "yellow" : "gray"}
                    leftSection={<IconHistory size={12} stroke={1.75} />}
                    styles={{ root: { cursor: "default" } }}
                  >
                    {ct.snapshot_count} snapshot{ct.snapshot_count === 1 ? "" : "s"}
                  </Badge>
                </Tooltip>
              )}
            </Group>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                Container ID <Text span ff="monospace" fw={600}>{ct.vmid}</Text>
              </Text>
              <Text size="xs" c="dimmed">
                · Node <Text span ff="monospace" fw={600}>{ct.node}</Text>
              </Text>
              <Text size="xs" c="dimmed">
                · Uptime <Text span fw={600}>{formatUptime(ct.uptime_seconds)}</Text>
              </Text>
            </Group>
          </Stack>

          <Group gap="xs" wrap="wrap">
            {ct.status === "stopped" && (
              <Tooltip label="Start" position="bottom" withArrow>
                <ActionIcon
                  size="lg"
                  variant="filled"
                  color="blue"
                  loading={busy}
                  onClick={() => void runAction("start")}
                  aria-label="Start"
                >
                  <IconPlayerPlay size={18} stroke={1.75} />
                </ActionIcon>
              </Tooltip>
            )}
            {ct.status === "running" && (
              <>
                <Tooltip label="Shutdown" position="bottom" withArrow>
                  <ActionIcon
                    size="lg"
                    variant="default"
                    loading={busy}
                    onClick={() => confirmPower("shutdown")}
                    aria-label="Shutdown"
                  >
                    <IconPower size={18} stroke={1.75} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Stop (force)" position="bottom" withArrow>
                  <ActionIcon
                    size="lg"
                    variant="light"
                    color="red"
                    loading={busy}
                    onClick={() => confirmPower("stop")}
                    aria-label="Stop (force)"
                  >
                    <IconPlayerStop size={18} stroke={1.75} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
            {ct.status === "running" ? (
              <Tooltip label="Console" position="bottom" withArrow>
                <ActionIcon
                  size="lg"
                  variant="default"
                  onClick={() => setConsoleOpen(true)}
                  aria-label="Console"
                >
                  <IconTerminal2 size={18} stroke={1.75} />
                </ActionIcon>
              </Tooltip>
            ) : (
              <Tooltip label="Start the container first" position="bottom" withArrow>
                <ActionIcon size="lg" variant="default" disabled aria-label="Start the container first">
                  <IconTerminal2 size={18} stroke={1.75} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip label="Resize" position="bottom" withArrow>
              <ActionIcon size="lg" variant="default" onClick={() => setResizeOpen(true)} aria-label="Resize">
                <IconAdjustments size={18} stroke={1.75} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Snapshots" position="bottom" withArrow>
              <ActionIcon
                size="lg"
                variant="default"
                onClick={() => setSnapshotsOpen(true)}
                aria-label="Snapshots"
              >
                <IconHistory size={18} stroke={1.75} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Delete" position="bottom" withArrow>
              <ActionIcon
                size="lg"
                variant="light"
                color="red"
                loading={busy}
                onClick={confirmDelete}
                aria-label="Delete"
              >
                <IconTrash size={18} stroke={1.75} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Card>

      {error && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />}>
          {error} (showing last known state)
        </Alert>
      )}

      {/* Resource cards — CPU / Memory / Swap / Status */}
      <SimpleGrid cols={{ base: 1, md: 4 }}>
        <ResourceCard
          icon={<IconCpu size={18} stroke={1.75} />}
          label="CPU"
          primary={`${ct.cpu_cores || "—"} cores`}
          usagePct={cpuUsagePct}
          usageLabel={cpuUsagePct !== null ? `${cpuUsagePct}% in use` : null}
          usageColor="brand"
        />
        <ResourceCard
          icon={<IconStack2 size={18} stroke={1.75} />}
          label="Memory"
          primary={ct.memory_mb ? formatRam(ct.memory_mb) : "—"}
          secondary={ct.memory_usage_bytes > 0 ? `${formatBytes(ct.memory_usage_bytes)} in use` : null}
          usagePct={memUsagePct}
          usageLabel={memUsagePct !== null ? `${memUsagePct}% in use` : null}
          usageColor="accent"
        />
        <ResourceCard
          icon={<IconArrowsExchange size={18} stroke={1.75} />}
          label="Swap"
          primary={ct.swap_mb ? formatRam(ct.swap_mb) : "—"}
          secondary={ct.swap_usage_bytes > 0 ? `${formatBytes(ct.swap_usage_bytes)} in use` : null}
          usagePct={swapUsagePct}
          usageLabel={swapUsagePct !== null ? `${swapUsagePct}% in use` : null}
          usageColor="accent"
        />
        <ResourceCard
          icon={<IconActivity size={18} stroke={1.75} />}
          label="Status"
          primary={<span style={{ textTransform: "capitalize" }}>{ct.status}</span>}
          secondary={`uptime ${formatUptime(ct.uptime_seconds)}`}
        />
      </SimpleGrid>

      {/* Disk activity (Phase 35) */}
      <Suspense fallback={null}>
        <DiskActivityCard kind="lxc" vmid={ct.vmid} />
      </Suspense>

      {/* Network */}
      <Card padding="lg">
        <Stack gap="sm">
          <Group gap="xs">
            <IconNetwork size={18} stroke={1.75} />
            <Title order={3}>Network</Title>
          </Group>
          {ct.ip_addresses.length === 0 ? (
            <Text c="dimmed" size="sm">
              No IP address assigned yet — the container may be stopped or still obtaining a DHCP lease.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={500}>
              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Interface</Table.Th>
                    <Table.Th>MAC</Table.Th>
                    <Table.Th>IPv4</Table.Th>
                    <Table.Th>IPv6</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {ct.ip_addresses.map((iface: GuestInterface) => (
                    <Table.Tr key={iface.name}>
                      <Table.Td>{iface.name}</Table.Td>
                      <Table.Td ff="monospace">{iface.mac || "—"}</Table.Td>
                      <Table.Td ff="monospace">{iface.ipv4 || "—"}</Table.Td>
                      <Table.Td ff="monospace">{iface.ipv6 || "—"}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Card>

      {/* Disks */}
      <Card padding="lg">
        <Stack gap="sm">
          <Group gap="xs">
            <IconDatabase size={18} stroke={1.75} />
            <Title order={3}>Disks</Title>
            <Badge variant="light" color="gray">
              {formatBytes(ct.disk_total_bytes)}
            </Badge>
          </Group>
          {ct.disks.length === 0 ? (
            <Text c="dimmed" size="sm">
              No disk information.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={520}>
              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Mount</Table.Th>
                    <Table.Th>Size</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {ct.disks.map((d) => (
                    <Table.Tr key={d.key}>
                      <Table.Td ff="monospace">{d.key}</Table.Td>
                      <Table.Td>{formatBytes(d.size_bytes)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Card>

      {/* Backups */}
      <BackupsCard kind="lxc" vmid={ct.vmid} name={ct.name} />

      {/* Metadata */}
      <Card padding="lg">
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <Stack gap="xs">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              OS type
            </Text>
            <Text>{ct.ostype ?? "—"}</Text>
          </Stack>
          <Stack gap="xs">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Tags
            </Text>
            <Group gap="xs">
              {ct.tags ? (
                ct.tags
                  .split(";")
                  .filter(Boolean)
                  .map((t) => (
                    <Badge key={t} variant="light">
                      {t}
                    </Badge>
                  ))
              ) : (
                <Text c="dimmed">—</Text>
              )}
            </Group>
          </Stack>
        </SimpleGrid>
      </Card>

      <ResizeContainerModal
        container={ct}
        opened={resizeOpen}
        onClose={() => setResizeOpen(false)}
        onResized={() => void load()}
      />
      <SnapshotsModal
        opened={snapshotsOpen}
        onClose={() => setSnapshotsOpen(false)}
        kind="container"
        vmid={ct.vmid}
        name={ct.name}
        onMutated={load}
      />
      <Suspense fallback={null}>
        <ContainerConsoleModal
          opened={consoleOpen}
          onClose={() => setConsoleOpen(false)}
          vmid={ct.vmid}
          name={ct.name}
        />
      </Suspense>
    </Stack>
  );
}

/**
 * Small resource card with optional usage bar. Used for CPU / Memory /
 * Swap / Status panels. `usagePct` (0-100) renders a colored bar; null
 * omits it.
 */
function ResourceCard({
  icon,
  label,
  primary,
  secondary,
  usagePct,
  usageLabel,
  usageColor,
}: {
  icon: React.ReactNode;
  label: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  usagePct?: number | null;
  usageLabel?: string | null;
  usageColor?: "brand" | "accent" | "green";
}) {
  return (
    <Card padding="lg">
      <Stack gap="sm">
        <Group gap="xs" align="center">
          <Text c="dimmed">{icon}</Text>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: "0.04em" }}>
            {label}
          </Text>
        </Group>
        <Stack gap={2}>
          <Text size="xl" fw={700}>
            {primary}
          </Text>
          {secondary && (
            <Text size="sm" c="dimmed">
              {secondary}
            </Text>
          )}
        </Stack>
        {usagePct !== null && usagePct !== undefined && (
          <Stack gap={4} mt={2}>
            <Progress
              value={usagePct}
              size="sm"
              color={usageColor ?? "brand"}
              radius="xs"
              styles={{ section: { transition: "width 400ms ease" } }}
            />
            {usageLabel && (
              <Text size="xs" c="dimmed">
                {usageLabel}
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
